import type { RangeField } from '@aquilixshop/ui-react';

/**
 * WHERE A SLIDER STOPS, AND WHERE THE SELLER STOPS (20/09/2026).
 *
 * « dans tous les composants où on a du texte, on doit pas limiter le user à 24px ou à un nombre de
 * pixel, on doit lui laisser faire ce qu'il veut, même 100px s'il veut il peut la faire » (fondatrice).
 *
 * Every "Text size" slider in the catalogue ends around 20-24px — 144 of them, written one section at a
 * time, each one ending where a paragraph usually ends. Raising all 144 to 1000 would not fix it, it
 * would break the common case: a track spanning 10→1000 gives ~1px of travel per 10 sizes, and the 14px
 * that answers nine cases out of ten becomes impossible to land on.
 *
 * So the slider keeps its comfortable span and stops being the LIMIT. The readout beside it — a `<span>`
 * until today — becomes the input, and on a free field it takes any number: the track then stretches to
 * hold it, so the handle still means something and the seller can drag on from there. See `RangeField.bounds`.
 *
 * The ceilings below are the only limit left, and they are here for one reason: an input that accepts
 * `1e9` paints a page nobody can scroll back from. They are far past anything a store needs.
 */
export interface Span {
  min: number;
  max: number;
}

/**
 * The absolute floor/ceiling a TYPED value is held to, per unit. A length or a multiplier is free —
 * that is the whole family of settings the founder's sentence is about (sizes, paddings, icons,
 * the theme's heading scale). Everything else — `%`, `deg`, `s`, `ms`, `words` — is strict, because
 * past its range it does not mean anything.
 */
const FREE_UNITS: Readonly<Record<string, Span>> = {
  px: { min: 0, max: 1000 },
  em: { min: -20, max: 20 },
  '×': { min: 0.1, max: 20 },
};

/**
 * HOW FAR THE HANDLE ITSELF GOES (21/09/2026).
 *
 * The typed box was not enough. The founder pushed « Taille du texte » to the end of its track, read 24,
 * and said it again: « ici le text size on doit pas bloquer le user à 24px, si il veut faire 100px il
 * peut ». A box that LOOKS like a readout is not an invitation to type — the gesture she makes is to
 * drag, and the drag must reach 100 on its own.
 *
 * So a free pixel field's track runs at least this far, whatever its schema says. 100 keeps the handle
 * usable (a 10→100 track still lands a 14 by hand and by arrow key) where 1000 would not, and the box
 * still takes anything up to the unit's ceiling for the rest.
 */
const TRACK_REACH: Readonly<Record<string, number>> = {
  px: 100,
};

/**
 * AND HOW FAR DOWN IT GOES (21/09/2026, later the same day).
 *
 * The other end of the same track: every heading size in the catalogue — the "Title" block, the section
 * headings of sixty sections — STARTS at 24px, so the handle would not go below it. « on peut diminuer
 * le titre moins que 24px ? mnt le builder nous donne un min de 24px » (fondatrice). Same answer, same
 * gesture: a free pixel track starts at most here, whatever its schema says. 8px is where text stops
 * being text; the box still takes anything down to the unit's floor.
 */
const TRACK_FLOOR: Readonly<Record<string, number>> = {
  px: 8,
};

/**
 * The floor the handle actually gets, kept ON THE SCHEMA'S STEP GRID. A range input counts its steps from
 * `min`: moving a 24-step-3 track down to 8 would make 24 itself — and the 39 default — unreachable by
 * drag, and the browser would repaint the value on the nearest step under the seller. So the floor is
 * the lowest `min - k·step` that is still at or above the unit's floor (24, step 3 → 9).
 */
function alignedFloor(field: RangeField, floor: number): number {
  if (field.min <= floor) return field.min;
  const step = field.step ?? 1;
  const steps = Math.floor((field.min - floor) / step);
  return field.min - steps * step;
}

/** Does this field's `min`/`max` bind the seller, or only the handle? */
export function isFree(field: RangeField): boolean {
  if (field.bounds !== undefined) return field.bounds === 'free';
  return field.unit !== undefined && field.unit in FREE_UNITS;
}

/**
 * What the seller may TYPE. On a strict field that is exactly the slider's range; on a free one it is
 * the unit's ceiling, widened if the schema itself already asks for more (the header's "Store name
 * size" runs to 240px, and a ceiling must never be below a span a schema shipped with).
 *
 * A free field with no unit is a ratio someone marked by hand (`bounds: 'free'`): ten times its span
 * upward, and downward only as far as it already goes — a line height of 0.9 is already tight enough
 * to overlap two lines, and nothing sensible lives below zero.
 */
export function typedSpan(field: RangeField): Span {
  if (!isFree(field)) return { min: field.min, max: field.max };
  const byUnit = field.unit !== undefined ? FREE_UNITS[field.unit] : undefined;
  if (byUnit) return { min: Math.min(byUnit.min, field.min), max: Math.max(byUnit.max, field.max) };
  return { min: field.min < 0 ? field.min * 10 : 0, max: field.max * 10 };
}

/**
 * Where the HANDLE stops before anything is typed: the schema's span, stretched to the unit's reach and
 * lowered to the unit's floor on a free field. A schema already running past the reach (the header's
 * 240px store name) keeps its own ceiling; one already starting under the floor keeps its own start.
 */
export function trackSpan(field: RangeField): Span {
  if (!isFree(field) || field.unit === undefined) return { min: field.min, max: field.max };
  const reach = TRACK_REACH[field.unit];
  const floor = TRACK_FLOOR[field.unit];
  return {
    min: floor === undefined ? field.min : alignedFloor(field, floor),
    max: reach === undefined ? field.max : Math.max(field.max, reach),
  };
}

/**
 * How many decimals the field speaks in. A typed number is rounded to the PRECISION of the step, never
 * snapped to a MULTIPLE of it: a step of 5 exists so the handle moves in fives, not to refuse the 37 the
 * seller typed. A whole step means a whole number — "16.4px" is noise nobody asked for.
 */
export function roundTyped(field: RangeField, n: number): number {
  const step = field.step ?? 1;
  if (Number.isInteger(step)) return Math.round(n);
  const decimals = (String(step).split('.')[1] ?? '').length;
  return Number(n.toFixed(decimals));
}

/** A typed number, rounded and held inside `typedSpan` — what actually gets committed. */
export function commitTyped(field: RangeField, n: number): number {
  const span = typedSpan(field);
  return Math.min(span.max, Math.max(span.min, roundTyped(field, n)));
}
